<template>
  <div>
    <!-- Batch Barcode Print Modal -->
    <app-custom-dialog
      v-model="dialog"
      :footer="false"
      title="print_barcodes_a4"
      width="1000"
    >
      <div slot="activator">
        <v-row>
          <!-- Left: Selected products & quantities -->
          <v-col cols="12" md="7">
            <div class="d-flex align-center mb-2">
              <span class="font-weight-medium text-caption">
                {{ $t("selected_items_count", { count: products.length }) }}
              </span>
              <v-spacer />
              <v-text-field
                v-model.number="applyAllQty"
                type="number"
                dense
                outlined
                hide-details
                :min="1"
                style="max-width: 70px"
                class="mr-2"
              />
              <v-btn small text color="primary" @click="applyQuantityToAll">
                {{ $t("apply_to_all") || "Apply to All" }}
              </v-btn>
            </div>

            <v-card outlined class="quantity-list">
              <div
                v-for="product in products"
                :key="product.code"
                class="quantity-row d-flex align-center px-3 py-2"
              >
                <div class="quantity-row-barcode-mini mr-3 flex-shrink-0">
                  <app-barcode
                    :item="product"
                    :width="1.5"
                    :height="36"
                    :display-value="false"
                    :show-name="false"
                    :show-price="false"
                    :show-size="false"
                    :show-code="false"
                    :margin-top="0"
                    :margin-bottom="0"
                    :margin-left="0"
                    :margin-right="0"
                    format="CODE128"
                  />
                </div>
                <div class="flex-grow-1 overflow-hidden mr-2">
                  <div class="text-caption font-weight-medium text-truncate">
                    {{ product.name }}
                  </div>
                  <div class="text-caption grey--text text-truncate">
                    {{ product.code }}
                  </div>
                </div>
                <v-btn
                  icon
                  x-small
                  :disabled="(quantities[product.code] || 1) <= 1"
                  @click="decrementQty(product.code)"
                >
                  <v-icon x-small>fal fa-minus</v-icon>
                </v-btn>
                <v-text-field
                  v-model.number="quantities[product.code]"
                  type="number"
                  dense
                  outlined
                  hide-details
                  :min="1"
                  class="mx-1 text-center quantity-input"
                  style="max-width: 60px"
                />
                <v-btn icon x-small @click="incrementQty(product.code)">
                  <v-icon x-small>fal fa-plus</v-icon>
                </v-btn>
              </div>
            </v-card>
          </v-col>

          <!-- Right: Settings Controls -->
          <v-col cols="12" md="5">
            <v-card outlined class="settings-panel">
              <v-expansion-panels
                v-model="panels"
                flat
                accordion
                multiple
                class="expansion-panels-compact"
              >
                <!-- Section 1: Label Layout -->
                <v-expansion-panel>
                  <v-expansion-panel-header class="py-2 px-2 min-height-auto">
                    <div class="d-flex align-center">
                      <v-icon x-small color="primary" class="mr-2"
                        >fal fa-th</v-icon
                      >
                      <span class="font-weight-medium text-caption">{{
                        $t("label_size") || "Label Layout"
                      }}</span>
                    </div>
                    <template v-slot:actions="{ isExpanded }">
                      <v-icon x-small>{{
                        isExpanded ? "fal fa-chevron-up" : "fal fa-chevron-down"
                      }}</v-icon>
                    </template>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-card-text class="pa-0">
                      <div class="d-flex align-center mb-1">
                        <span class="text-caption grey--text">{{
                          $t("label_width") || "Label Width"
                        }}</span>
                      </div>
                      <v-row no-gutters class="mb-2">
                        <v-col cols="12">
                          <v-text-field
                            v-model.number="settings.labelWidth"
                            type="number"
                            dense
                            outlined
                            hide-details
                            :min="20"
                            :max="105"
                            suffix="mm"
                          ></v-text-field>
                        </v-col>
                      </v-row>

                      <div class="d-flex align-center mb-1">
                        <span class="text-caption grey--text">{{
                          $t("label_spacing") || "Spacing"
                        }}</span>
                      </div>
                      <v-row no-gutters class="mb-2">
                        <v-col cols="6" class="pr-1">
                          <v-text-field
                            v-model.number="settings.columnGap"
                            type="number"
                            dense
                            outlined
                            hide-details
                            :min="0"
                            :max="30"
                            suffix="mm"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="6" class="pl-1">
                          <v-text-field
                            v-model.number="settings.rowGap"
                            type="number"
                            dense
                            outlined
                            hide-details
                            :min="0"
                            :max="30"
                            suffix="mm"
                          ></v-text-field>
                        </v-col>
                      </v-row>

                      <div class="d-flex align-center mb-1">
                        <span class="text-caption grey--text">{{
                          $t("page_margin") || "Page Margin"
                        }}</span>
                      </div>
                      <v-text-field
                        v-model.number="settings.pageMargin"
                        type="number"
                        dense
                        outlined
                        hide-details
                        :min="0"
                        :max="40"
                        suffix="mm"
                      ></v-text-field>
                    </v-card-text>

                    <v-card-text class="pa-0 mt-2">
                      <div class="d-flex align-center mb-1">
                        <span class="text-caption grey--text">{{
                          $t("display") || "Display"
                        }}</span>
                      </div>
                      <v-checkbox
                        v-model="settings.showName"
                        dense
                        hide-details
                        :label="$t('product_name') || 'Product Name'"
                        class="my-0"
                      ></v-checkbox>
                      <v-checkbox
                        v-model="settings.showPrice"
                        dense
                        hide-details
                        :label="$t('price') || 'Price'"
                        class="my-0"
                      ></v-checkbox>
                      <v-checkbox
                        v-model="settings.showCutLines"
                        dense
                        hide-details
                        :label="$t('show_cut_lines') || 'Cutting Lines'"
                        class="my-0"
                      ></v-checkbox>
                    </v-card-text>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Section 2: Barcode Size -->
                <v-expansion-panel>
                  <v-expansion-panel-header class="py-2 px-2 min-height-auto">
                    <div class="d-flex align-center">
                      <v-icon x-small color="primary" class="mr-2"
                        >fal fa-arrows-alt</v-icon
                      >
                      <span class="font-weight-medium text-caption">{{
                        $t("barcode_size") || "Barcode Size"
                      }}</span>
                    </div>
                    <template v-slot:actions="{ isExpanded }">
                      <v-icon x-small>{{
                        isExpanded ? "fal fa-chevron-up" : "fal fa-chevron-down"
                      }}</v-icon>
                    </template>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="pt-2">
                    <v-card-text class="pa-0">
                      <div class="d-flex align-center mb-1">
                        <span class="text-caption grey--text">{{
                          $t("bar_width") || "Bar Width (mm)"
                        }}</span>
                      </div>
                      <div class="d-flex align-center">
                        <v-slider
                          v-model="settings.width"
                          min="0.5"
                          max="10"
                          step="0.1"
                          dense
                          hide-details
                          class="flex-grow-1 mr-2"
                          thumb-size="14"
                        ></v-slider>
                        <v-text-field
                          v-model.number="settings.width"
                          type="number"
                          dense
                          outlined
                          hide-details
                          :min="0.5"
                          :max="10"
                          step="0.1"
                          style="max-width: 65px"
                        ></v-text-field>
                      </div>
                    </v-card-text>

                    <v-card-text class="pa-0">
                      <div class="d-flex align-center mb-1">
                        <span class="text-caption grey--text">{{
                          $t("bar_height") || "Bar Height (mm)"
                        }}</span>
                      </div>
                      <div class="d-flex align-center">
                        <v-slider
                          v-model="settings.height"
                          min="5"
                          max="100"
                          step="1"
                          dense
                          hide-details
                          class="flex-grow-1 mr-2"
                          thumb-size="14"
                        ></v-slider>
                        <v-text-field
                          v-model.number="settings.height"
                          type="number"
                          dense
                          outlined
                          hide-details
                          :min="5"
                          :max="100"
                          step="1"
                          style="max-width: 65px"
                        ></v-text-field>
                      </div>
                    </v-card-text>

                    <v-card-text class="pa-0">
                      <div class="d-flex align-center mb-1">
                        <span class="text-caption grey--text">{{
                          $t("font_size_px") || "Font Size (px)"
                        }}</span>
                      </div>
                      <div class="d-flex align-center">
                        <v-slider
                          v-model="settings.fontSize"
                          min="6"
                          max="48"
                          dense
                          hide-details
                          class="flex-grow-1 mr-2"
                          thumb-size="14"
                        ></v-slider>
                        <v-text-field
                          v-model.number="settings.fontSize"
                          type="number"
                          dense
                          outlined
                          hide-details
                          :min="6"
                          :max="48"
                          style="max-width: 75px"
                        ></v-text-field>
                      </div>
                    </v-card-text>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card>
          </v-col>
        </v-row>

        <!-- Action Buttons -->
        <v-row class="mt-2">
          <v-col cols="12" class="d-flex align-center justify-end">
            <span class="grey--text text-caption mr-4">
              {{ $t("total_labels") || "Total Labels" }}: {{ totalLabels }}
            </span>
            <v-btn text @click="close" class="mr-2">{{
              $t("close") || "Close"
            }}</v-btn>
            <v-btn
              color="primary"
              @click="print"
              :loading="printing"
              :disabled="products.length === 0"
              depressed
              class="rounded-lg"
            >
              <v-icon left small>fal fa-print</v-icon>
              {{ $t("print") || "Print" }}
              <v-chip x-small class="ml-2 print-count-chip">{{
                totalLabels
              }}</v-chip>
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </app-custom-dialog>

    <!-- Hidden Print Container -->
    <div
      v-if="products && products.length > 0"
      ref="printContainer"
      :key="`batch-print-container-${totalLabels}`"
      style="display: none"
    >
      <div
        v-for="(product, idx) in printItems"
        :key="`${product.code}-${idx}`"
        class="barcode-print-item"
      >
        <app-barcode :item="product" v-bind="barcodeSettings" />
      </div>
    </div>
  </div>
</template>

<script>
import AppBarcode from "@/components/barcode";

export default {
  components: {
    AppBarcode,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    products: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      dialog: false,
      printing: false,
      applyAllQty: 1,
      quantities: {},
      panels: [0],
      settings: {
        // Typography
        fontSize: 10,
        fontOptions: "normal",
        textAlign: "center",
        textMargin: 3,
        displayValue: true,
        // Barcode dimensions (in mm)
        width: 1.5,
        height: 30,
        lineColor: "#000000",
        background: "#ffffff",
        // Margins (in mm)
        marginTop: 3,
        marginBottom: 3,
        marginLeft: 3,
        marginRight: 3,
        // Content display
        showName: true,
        showPrice: true,
        showSize: false,
        showCode: false,
        showCutLines: true,
        // Barcode format
        format: "CODE128",
        // Layout (in mm) - page is fixed A4 (210 x 297mm)
        labelWidth: 50,
        columnGap: 4,
        rowGap: 4,
        pageMargin: 10,
      },
    };
  },
  computed: {
    barcodeSettings() {
      return {
        fontSize: this.settings.fontSize,
        fontOptions: this.settings.fontOptions,
        textAlign: this.settings.textAlign,
        textMargin: this.settings.textMargin,
        displayValue: this.settings.displayValue,
        width: this.settings.width,
        height: this.settings.height,
        lineColor: this.settings.lineColor,
        background: this.settings.background,
        marginTop: this.settings.marginTop,
        marginBottom: this.settings.marginBottom,
        marginLeft: this.settings.marginLeft,
        marginRight: this.settings.marginRight,
        showName: this.settings.showName,
        showPrice: this.settings.showPrice,
        showSize: this.settings.showSize,
        showCode: this.settings.showCode,
        format: this.settings.format,
        selected: [],
      };
    },
    totalLabels() {
      return this.products.reduce(
        (sum, p) => sum + Math.max(1, parseInt(this.quantities[p.code]) || 1),
        0
      );
    },
    printItems() {
      const items = [];
      this.products.forEach((p) => {
        const qty = Math.max(1, parseInt(this.quantities[p.code]) || 1);
        for (let i = 0; i < qty; i++) {
          items.push(p);
        }
      });
      return items;
    },
  },
  watch: {
    value(val) {
      this.dialog = val;
      if (val) {
        this.loadSettings();
        this.initQuantities();
      }
    },
    dialog(val) {
      this.$emit("input", val);
    },
    products() {
      this.initQuantities();
    },
    settings: {
      handler() {
        this.saveSettings();
      },
      deep: true,
    },
  },
  methods: {
    close() {
      this.dialog = false;
    },
    initQuantities() {
      const q = {};
      this.products.forEach((p) => {
        q[p.code] = this.quantities[p.code] || 1;
      });
      this.quantities = q;
    },
    incrementQty(code) {
      this.$set(this.quantities, code, (this.quantities[code] || 1) + 1);
    },
    decrementQty(code) {
      const current = this.quantities[code] || 1;
      if (current > 1) {
        this.$set(this.quantities, code, current - 1);
      }
    },
    applyQuantityToAll() {
      const qty = Math.max(1, parseInt(this.applyAllQty) || 1);
      this.products.forEach((p) => {
        this.$set(this.quantities, p.code, qty);
      });
    },
    async print() {
      this.printing = true;
      try {
        if (!this.products || this.products.length === 0) {
          throw new Error("No products selected for printing");
        }

        const expectedCount = this.totalLabels;

        await this.$nextTick();

        const printContainer = this.$refs.printContainer;
        if (!printContainer) {
          throw new Error("Print container not found");
        }

        await this.$nextTick();
        await new Promise((resolve) => setTimeout(resolve, 200));

        let printItemEls = printContainer.querySelectorAll(
          ".barcode-print-item"
        );
        if (printItemEls.length !== expectedCount) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          printItemEls = printContainer.querySelectorAll(".barcode-print-item");
          if (printItemEls.length !== expectedCount) {
            throw new Error(
              `Failed to render correct number of barcodes. Expected ${expectedCount}, found ${printItemEls.length}`
            );
          }
        }

        const clonedContainer = printContainer.cloneNode(true);
        const cleanHTML = this.cleanVueHTML(clonedContainer.innerHTML);

        const printWindow = window.open("", "_blank");
        if (!printWindow) {
          throw new Error("Unable to open print window");
        }

        const pageMargin = this.settings.pageMargin || 10;
        const columnGap = this.settings.columnGap || 4;
        const rowGap = this.settings.rowGap || 4;
        const labelWidth = this.settings.labelWidth || 50;

        const printContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Print Barcodes (A4)</title>
            <meta charset="UTF-8">
            <link rel="preconnect" href="https://fonts.googleapis.com">
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
            <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Lao+Looped:wght@400;500;600;700&family=Inter:ital,opsz,wght@0,14..32,400..700;1,14..32,400..700&display=swap" rel="stylesheet">
            <style>
              @page {
                size: A4;
                margin: 0;
              }
              * {
                box-sizing: border-box;
                margin: 0;
                padding: 0;
              }
              body {
                width: 210mm;
                font-family: 'Noto Sans Lao Looped', 'Inter', Arial, sans-serif;
              }
              .print-page {
                width: 210mm;
                padding: ${pageMargin}mm;
              }
              .print-grid {
                display: flex;
                flex-wrap: wrap;
                gap: ${rowGap}mm ${columnGap}mm;
              }
              .barcode-print-item {
                width: ${labelWidth}mm;
                display: flex;
                justify-content: center;
                align-items: center;
                break-inside: avoid;
                page-break-inside: avoid;
                box-sizing: border-box;
                ${
                  this.settings.showCutLines !== false
                    ? "border: 1px dashed #999999;"
                    : ""
                }
              }
              .barcode-label {
                text-align: center;
                font-size: ${this.settings.fontSize || 10}px;
                color: #000000;
                width: 100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                box-sizing: border-box;
              }
              .barcode-text {
                font-family: 'Noto Sans Lao Looped', 'Inter', Arial, sans-serif;
                line-height: 1.2;
                word-wrap: break-word;
                margin-bottom: ${this.settings.textMargin || 3}px;
                max-width: 100%;
                width: 100%;
                text-align: center;
              }
              .barcode-bold {
                font-weight: bold;
              }
              .barcode-italic {
                font-style: italic;
              }
              .barcode-svg {
                display: block;
                width: fit-content;
                max-width: 100%;
                margin: 0 auto;
                page-break-inside: avoid;
              }
              .barcode-svg svg {
                display: block;
                max-width: 100%;
                height: auto;
                width: auto;
                margin: 0 auto;
                page-break-inside: avoid;
              }
            </style>
          </head>
          <body>
            <div class="print-page">
              <div class="print-grid">
                ${cleanHTML}
              </div>
            </div>
          </body>
          </html>
        `;

        printWindow.document.write(printContent);
        printWindow.document.close();

        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            this.printing = false;
          }, 300);
        };

        this.$emit("print", {
          totalLabels: expectedCount,
          products: this.products,
          settings: this.settings,
        });
      } catch (error) {
        console.error("[Batch Barcode Print] Print error:", error);
        this.$toast.error(this.$t("print_error") || "Error printing barcodes");
        this.printing = false;
      }
    },
    cleanVueHTML(html) {
      return html
        .replace(/<!--[\s\S]*?-->/g, "")
        .replace(/\s+/g, " ")
        .trim();
    },
    loadSettings() {
      try {
        const saved = localStorage.getItem("barcodeBatchPrintSettings");
        if (saved) {
          const settings = JSON.parse(saved);
          this.settings = { ...this.settings, ...settings };
        }
      } catch (error) {
        console.error("Error loading batch barcode settings:", error);
      }
    },
    saveSettings() {
      try {
        localStorage.setItem(
          "barcodeBatchPrintSettings",
          JSON.stringify(this.settings)
        );
      } catch (error) {
        console.error("Error saving batch barcode settings:", error);
      }
    },
  },
};
</script>

<style scoped>
.quantity-list {
  max-height: 500px;
  overflow-y: auto;
}

.quantity-row:not(:last-child) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.quantity-row-barcode-mini {
  width: 110px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.quantity-row-barcode-mini ::v-deep .barcode-label {
  padding: 0;
}

.quantity-row-barcode-mini ::v-deep .barcode-svg {
  max-width: 100%;
  max-height: 100%;
}

.quantity-row-barcode-mini ::v-deep .barcode-svg svg {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
}

.settings-panel {
  max-height: 500px;
  overflow-y: auto;
}

.quantity-input >>> input {
  text-align: center;
}

.print-count-chip {
  background: rgba(255, 255, 255, 0.25) !important;
  color: #fff !important;
}

.theme--dark .quantity-row:not(:last-child) {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}
</style>
